// A live delta subscription that moves with its watermark.
//
// The delta queries read `updated_at > since` and take one page. Opened once
// with no `since`, a subscription reads the OLDEST page of the table, and a
// reactive query only re-runs when something inside what it read changes. So
// once a table outgrows a page (a child with more than 500 messages), a new
// or edited row lands past the read range and never reaches the open app:
// a reply sits on the server while the page waits for it until a reload.
//
// Following the watermark keeps the read range on the newest rows. After each
// page the subscription reopens from where that page ended (a partial page
// continues exactly there), holding back `lagMs` once caught up: a mutation
// stamps `updated_at` when it starts, so a row can commit just after a later
// stamped one, and the window keeps such a row inside what the next query
// reads. Rows in the window are delivered again; the store's merge is
// idempotent.

export interface FollowedPage {
  rows?: unknown[]
  nextSince?: unknown
  hasMore?: boolean
}

export function followDelta<P extends FollowedPage>(
  open: (args: Record<string, unknown>, onPage: (page: P) => void) => () => void,
  args: Record<string, unknown>,
  onPage: (page: P) => void,
  lagMs = 5_000,
): () => void {
  let since = typeof args.since === 'number' ? args.since : undefined
  let closed = false
  let unsubscribe: (() => void) | undefined

  const listen = () => {
    unsubscribe = open(since === undefined ? args : { ...args, since }, (page) => {
      if (closed) return
      // This subscription drains its own pages, so nobody else should page through.
      onPage({ ...page, hasMore: false })
      const next = page?.nextSince
      if (typeof next !== 'number') return
      const target = page.hasMore ? next : Math.max(since ?? 0, next - lagMs)
      if (since !== undefined && target <= since) return
      // Reopen outside the callback the client is still running.
      queueMicrotask(() => {
        if (closed) return
        const previous = unsubscribe
        since = target
        listen()
        previous?.()
      })
    })
  }

  listen()
  return () => {
    closed = true
    unsubscribe?.()
  }
}
